import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Shield, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const { t } = useLanguage();

  const carriers = [
    "MassMutual", "New York Life", "Northwestern Mutual", "Pacific Life",
    "Guardian Life", "Lincoln Financial", "Penn Mutual", "Protective Life"
  ];

  return (
    <footer className="bg-[#020617] border-t border-slate-800">
      {/* Carrier Logos Section */}
      <div className="border-b border-slate-800/50 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-xs uppercase tracking-widest text-slate-500 mb-8">
            Trusted Partners • A+ / A++ Rated Carriers
          </p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
            {carriers.map((carrier) => (
              <span
                key={carrier}
                className="text-slate-600 text-sm font-serif uppercase tracking-wider hover:text-[#C5A059] transition-colors cursor-default"
              >
                {carrier}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <span className="text-2xl font-bold tracking-tight">
                <span className="text-[#C5A059]">Shop</span>
                <span className="text-white">CompareInsure</span>
              </span>
            </Link>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              Your trusted partner for life insurance comparison at ShopCompareInsure.com and premium financing solutions.
            </p>
            <div className="flex items-center space-x-2 text-slate-400">
              <Shield className="w-4 h-4 text-[#C5A059]" />
              <span className="text-xs uppercase tracking-wider">Secure & Confidential</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { path: '/compare', label: 'Compare Quotes' },
                { path: '/premium-financing', label: 'Premium Financing' },
                { path: '/carriers', label: 'Our Carriers' },
                { path: '/education', label: 'Education Center' },
                { path: '/blog', label: 'Insights & Articles' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-500 hover:text-[#C5A059] text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              {[
                'Term Life Insurance',
                'Whole Life Insurance',
                'Universal Life Insurance',
                'Premium Financing',
                'Estate Planning Guidance',
              ].map((service) => (
                <li key={service}>
                  <span className="text-slate-500 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Mail className="w-4 h-4 text-[#C5A059] mt-1 flex-shrink-0" />
                <span className="text-slate-500 text-sm">contact@shopcompareinsure.com</span>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-4 h-4 text-[#C5A059] mt-1 flex-shrink-0" />
                <span className="text-slate-500 text-sm">+1 (469) 789-3603</span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-[#C5A059] mt-1 flex-shrink-0" />
                <span className="text-slate-500 text-sm">Global Advisory Services</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          {/* Disclaimer */}
          <p className="text-xs text-slate-600 leading-relaxed mb-6 max-w-4xl" data-testid="footer-disclaimer">
            {t('footer.disclaimer')}
          </p>
          
          {/* Copyright & Links */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-600">
              © {new Date().getFullYear()} ShopCompareInsure. {t('footer.rights')}
            </p>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="text-xs text-slate-600 hover:text-[#C5A059] transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/terms" className="text-xs text-slate-600 hover:text-[#C5A059] transition-colors">
                {t('footer.terms')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
